import type { Case, CaseConversation, CaseDocument } from '~/stores/cases'
import type { ChatScope } from '~~/shared/web-chat/scope'
import { sameScope } from '~~/shared/web-chat/scope'
import { isFiledUnderCase } from '~~/shared/chat/conversation'

/**
 * The payload behind the right-hand context rail (MAR-115) — "which space am I in, and what is in it".
 *
 * **The rail follows `webChat.activeScope`, not the route.** The scope union already models exactly
 * the two rungs the rail shows (`client` / `case` with ids and names), it survives navigation, and it
 * is what actually bounds the agent's document access — so the rail answers "what can Marcus see"
 * rather than "what page am I on". Following the route instead would leave the rail blank everywhere
 * outside `/app/clients/**`, which is most of the app.
 *
 * One request per rung: `getCase` already returns the documents and every count in a single row, and
 * `getClient` + `listCases` cover the client rung. Nothing here re-derives what the case hub loads —
 * the rail is a summary surface and deliberately does not try to be the hub.
 */
export interface ContextRailData {
  scope: ChatScope
  caseItem: Case | null
  client: { id: string; name: string; case_count?: number } | null
  cases: Case[]
  documents: CaseDocument[]
  conversations: CaseConversation[]
}

/**
 * What the rail actually STORES (MAR-125): a scope, the ids it resolved, and the two payloads that
 * are not entities.
 *
 * `caseItem` and `cases` used to be held here as full copies, which meant renaming a case left the
 * rail showing the old name until the scope changed. They are now ids resolved through the cases and
 * clients stores, so the rail cannot disagree with the rest of the app about what a case is called.
 *
 * `documents` and `conversations` stay by value: the document entries are case-LINK rows
 * (`link_id`, `linked_at`) rather than documents, and conversations belong to the chats domain.
 */
interface ContextRailState {
  scope: ChatScope
  caseId: string | null
  clientId: string | null
  caseIds: string[]
  documents: CaseDocument[]
  conversations: CaseConversation[]
}

const EMPTY: ContextRailState = {
  scope: { kind: 'global' },
  caseId: null,
  clientId: null,
  caseIds: [],
  documents: [],
  conversations: [],
}

export function useContextRail() {
  const webChat = useWebChatStore()
  const casesStore = useCasesStore()
  const clientsStore = useClientsStore()
  const { getCase, listCases, listCaseConversations } = casesStore
  const { getClient } = clientsStore

  const state = useState<ContextRailState>('context-rail', () => ({ ...EMPTY }))
  const loading = useState<boolean>('context-rail-loading', () => false)

  const data = computed<ContextRailData>(() => {
    const s = state.value
    const caseItem = s.caseId ? casesStore.get(s.caseId) : null
    // On the case rung the client is named by the case row itself (there is no separate fetch); on
    // the client rung it comes from the clients store.
    const client = caseItem?.client_id
      ? { id: caseItem.client_id, name: caseItem.client_name ?? '' }
      : s.clientId
        ? (() => {
            const c = clientsStore.get(s.clientId!)
            return c ? { id: c.id, name: c.name, case_count: c.case_count } : null
          })()
        : null
    return {
      scope: s.scope,
      caseItem,
      client,
      cases: s.caseIds.flatMap((id) => { const c = casesStore.get(id); return c ? [c] : [] }),
      documents: s.documents,
      conversations: s.conversations,
    }
  })

  /**
   * Guards against a slow response for a scope the user has already left overwriting a fast one for
   * the scope they are now in. The scope at request time is compared with the live scope on arrival;
   * `sameScope` compares the rung and the id, which is the whole identity of a scope.
   */
  async function load(scope: ChatScope) {
    // Client-only: the scope itself is restored from localStorage after hydration, so there is
    // nothing to fetch during SSR and an unauthenticated server-side call would only burn a request.
    if (!import.meta.client) return
    if (scope.kind === 'global') {
      state.value = { ...EMPTY }
      loading.value = false
      return
    }

    loading.value = true
    try {
      if (scope.kind === 'case') {
        // `getCase` caches the row (and seeds its documents into the documents store), so only the
        // id is kept here — the name the rail renders comes from the cases store.
        const [caseItem, conversations] = await Promise.all([
          getCase(scope.id),
          listCaseConversations(scope.id).catch(() => [] as CaseConversation[]),
        ])
        if (!sameScope(scope, webChat.activeScope)) return
        state.value = {
          scope,
          caseId: caseItem.id,
          clientId: caseItem.client_id,
          caseIds: [],
          documents: caseItem.documents ?? [],
          conversations,
        }
        return
      }

      const [client, cases] = await Promise.all([
        getClient(scope.id),
        listCases({ clientId: scope.id }).catch(() => [] as Case[]),
      ])
      if (!sameScope(scope, webChat.activeScope)) return
      state.value = {
        scope,
        caseId: null,
        clientId: client.id,
        caseIds: cases.map(c => c.id),
        documents: [],
        conversations: [],
      }
    } catch {
      // A failed load leaves the rail empty rather than half-populated: a stale case name next to a
      // new case's documents would be a worse answer to "where am I" than no answer.
      if (sameScope(scope, webChat.activeScope)) state.value = { ...EMPTY, scope }
    } finally {
      if (sameScope(scope, webChat.activeScope)) loading.value = false
    }
  }

  /**
   * A conversation just became current (created, or switched to). If it is FILED under the case the
   * rail is showing and the rail does not list it yet, refetch that list (MAR-1468).
   *
   * `load` runs only when the SCOPE changes, so a chat started inside a case never reached the rail
   * until the scope moved, and its count stayed one short. The test is the row's filing, never the
   * scope: the scope is what "Acotar" changes, and it no longer moves a conversation between cases.
   */
  async function refreshConversations(conversationId: string | null) {
    if (!import.meta.client || !conversationId) return
    const scope = state.value.scope
    if (scope.kind !== 'case' || state.value.conversations.some(c => c.id === conversationId)) return
    const row = useChatsStore().conversations.find(c => c.id === conversationId)
    if (!isFiledUnderCase(row, scope.id)) return
    const conversations = await listCaseConversations(scope.id).catch(() => null)
    if (conversations && sameScope(scope, state.value.scope)) {
      state.value = { ...state.value, conversations }
    }
  }

  return { data, loading, load, refreshConversations }
}
